/**
 * Canonical Customer.io event names.
 *
 * Every producer (storefront, server functions, notification channels) must
 * reference these constants — never a string literal. WhatsApp / push
 * channels added later reuse the exact same catalogue.
 */
export const CIO_EVENTS = {
  // Authentication
  USER_REGISTERED: "user_registered",
  USER_LOGIN: "user_login",
  USER_LOGOUT: "user_logout",
  PASSWORD_RESET_REQUESTED: "password_reset_requested",
  PASSWORD_RESET_COMPLETED: "password_reset_completed",
  EMAIL_VERIFIED: "email_verified",

  // Shopping
  HOMEPAGE_VIEWED: "homepage_viewed",
  PRODUCT_VIEWED: "product_viewed",
  COLLECTION_VIEWED: "collection_viewed",
  CATEGORY_VIEWED: "category_viewed",
  SEARCH_PERFORMED: "search_performed",
  ADD_TO_CART: "add_to_cart",
  REMOVE_FROM_CART: "remove_from_cart",
  CART_UPDATED: "cart_updated",
  WISHLIST_ADDED: "wishlist_added",
  WISHLIST_REMOVED: "wishlist_removed",

  // Checkout
  CHECKOUT_STARTED: "checkout_started",
  SHIPPING_SELECTED: "shipping_selected",
  COUPON_APPLIED: "coupon_applied",
  PAYMENT_STARTED: "payment_started",
  PAYMENT_SUCCESS: "payment_success",
  PAYMENT_FAILED: "payment_failed",

  // Orders
  ORDER_CREATED: "order_created",
  ORDER_CONFIRMED: "order_confirmed",
  ORDER_PROCESSING: "order_processing",
  ORDER_PACKED: "order_packed",
  ORDER_READY_FOR_PICKUP: "order_ready_for_pickup",
  ORDER_SHIPPED: "order_shipped",
  ORDER_OUT_FOR_DELIVERY: "order_out_for_delivery",
  ORDER_DELIVERED: "order_delivered",
  ORDER_COMPLETED: "order_completed",
  ORDER_RETURNED: "order_returned",
  ORDER_REFUNDED: "order_refunded",
  ORDER_CANCELLED: "order_cancelled",

  // Customer
  NEWSLETTER_SIGNUP: "newsletter_signup",
  CONTACT_FORM: "contact_form_submitted",
  PRODUCT_REVIEW: "product_review_submitted",
  CUSTOMER_BLOCKED: "customer_blocked",
  CUSTOMER_UNBLOCKED: "customer_unblocked",

  // Diagnostics
  TEST_EVENT: "cb16_test_event",
  TEST_EMAIL_EVENT: "cb16_test_email_event",
} as const;

export type CioEventName = (typeof CIO_EVENTS)[keyof typeof CIO_EVENTS];

export const CIO_EVENT_NAMES: CioEventName[] = Object.values(CIO_EVENTS);

/** Order status (existing workflow) → Customer.io event. */
export const ORDER_STATUS_EVENT: Record<string, CioEventName> = {
  pending: CIO_EVENTS.ORDER_CREATED,
  confirmed: CIO_EVENTS.ORDER_CONFIRMED,
  processing: CIO_EVENTS.ORDER_PROCESSING,
  packed: CIO_EVENTS.ORDER_PACKED,
  ready_for_pickup: CIO_EVENTS.ORDER_READY_FOR_PICKUP,
  shipped: CIO_EVENTS.ORDER_SHIPPED,
  out_for_delivery: CIO_EVENTS.ORDER_OUT_FOR_DELIVERY,
  delivered: CIO_EVENTS.ORDER_DELIVERED,
  completed: CIO_EVENTS.ORDER_COMPLETED,
  returned: CIO_EVENTS.ORDER_RETURNED,
  refunded: CIO_EVENTS.ORDER_REFUNDED,
  cancelled: CIO_EVENTS.ORDER_CANCELLED,
};

/**
 * Shared event payload contract. Every field is optional — producers send
 * whatever context they have and the transport strips undefined values.
 */
export type CioEventPayload = {
  customer_id?: string | null;
  email?: string | null;
  phone?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  order_id?: string | null;
  order_number?: string | null;
  subtotal?: number | null;
  shipping?: number | null;
  discount?: number | null;
  tax?: number | null;
  grand_total?: number | null;
  currency?: string | null;
  payment_method?: string | null;
  shipping_partner?: string | null;
  delivery_type?: string | null;
  tracking_number?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  device?: string | null;
  browser?: string | null;
  session_id?: string | null;
  timestamp?: string | null;
  [key: string]: unknown;
};